import { Instant } from "@js-joda/core";
import { toast } from "react-toastify";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import {
  Vehicle,
  SubListEntry,
} from "./Data/ApiTypes";
import { InviteParkerForm } from "./UserPlanDetailsPageInviteParkerModal";
import { UNASSIGNED_GROUP_ID } from "./SubscriptionManageGroupParkersPage";
import { parseParkerInfo } from "./Data/ApiParse";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { localDateToInstantUtc } from "./Data/Date";
import { createInstant } from "./Data/ApiConverters";
import { isCancelDateToday } from "./Data/cancelParker";

type UseSubscriptionParkersActionsParams = {
  subscriptionId: number;
  setParkers: React.Dispatch<React.SetStateAction<any[]>>;
  selectedParker?: any | null;
  setSelectedParker?: React.Dispatch<React.SetStateAction<any | undefined>>;

  onNoSpaces?: () => void;
  setShowChooseVehicle: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAddVehicle?: React.Dispatch<React.SetStateAction<boolean>>;
  setChooseModalVehicles: React.Dispatch<React.SetStateAction<Vehicle[]>>;
  setChooseModalExclude: React.Dispatch<React.SetStateAction<number[]>>;
  groups?: any[];
  setSubVehicles?: React.Dispatch<React.SetStateAction<Vehicle[]>>;
  ownerParkerId?: number;
  onAfterParkerChange?: () => void;
};

export function useSubscriptionParkersActions({
  subscriptionId,
  setParkers,
  selectedParker,
  setSelectedParker,
  onNoSpaces,
  setShowChooseVehicle,
  setShowAddVehicle,
  setChooseModalVehicles,
  setChooseModalExclude,
  groups,
  setSubVehicles,
  ownerParkerId,
  onAfterParkerChange,
}: UseSubscriptionParkersActionsParams) {
  async function parkerRemove(parkerId: number, subId?: number, cancellationDate?: string) {
    // When a cancellationDate (yyyy-MM-dd) is provided, send UTC midnight for that calendar day.
    // Otherwise remove immediately.
    const removedWhen = cancellationDate
      ? localDateToInstantUtc(cancellationDate)
      : Instant.now().toString();
    await Api.subParkerRemove(subscriptionId || subId!, {
      parkerId,
      removedWhen,
    }).then(
      (resp) =>
        processResult(
          resp,
          () => {
            const removeFromList =
              !cancellationDate || isCancelDateToday(cancellationDate);

            if (removeFromList) {
              setParkers((prev) =>
                prev.filter((p) => (p.parker?.parkerId ?? p.parkerId) !== parkerId)
              );
            } else {
              setParkers((prev) =>
                prev.map((p) =>
                  (p.parker?.parkerId ?? p.parkerId) === parkerId
                    ? { ...p, removedWhen: createInstant(removedWhen) }
                    : p
                )
              );
            }
            onAfterParkerChange?.();
          },
          (err) => toast.error(err)
        )
    );
  }

  async function parkerUndoCancel(parkerId: number, subId?: number) {
    await Api.subParkerUndoCancel(subscriptionId || subId!, { parkerId }).then(
      (resp) =>
        processResult(
          resp,
          () => {
            setParkers((prev) =>
              prev.map((p) =>
                (p.parker?.parkerId ?? p.parkerId) === parkerId
                  ? { ...p, removedWhen: null }
                  : p
              )
            );
            toast.success("Cancellation undone");
            onAfterParkerChange?.();
          },
          (err) => toast.error(err)
        )
    );
  }

  async function vehicleAdd(vehicle: Vehicle) {
    vehicle.parkerId = selectedParker?.parker.parkerId || ownerParkerId;

    const res = await Api.vehicleAdd(vehicle);
    if (!res.isOk) {
      toast.error(res.error);
      return;
    }

    const newVehicle = res.value;

    await vehicleLink(newVehicle);
  }

  async function vehicleLink(vehicle: Vehicle) {
    const res = await Api.subscriptionVehicleAdd(subscriptionId, {
      vehicleId: vehicle.vehicleId,
      parkerId: vehicle.parkerId,
    });
    console.log(res);
    if (!res.isOk) {
      toast.error(res.error);
      return;
    }

    if (!selectedParker || selectedParker.parker.parkerId === ownerParkerId) {
      setSubVehicles?.((prev) => [...prev, vehicle]);
    }

    setParkers((prev) =>
      prev.map((p) =>
        p.parker && p.parker.parkerId === vehicle.parkerId
          ? { ...p, vehicles: [...p.vehicles, vehicle] }
          : p
      )
    );

    setSelectedParker?.((prev) =>
      prev ? { ...prev, vehicles: [...prev.vehicles, vehicle] } : prev
    );

    toast.success("Vehicle Added Successfully");
  }

  async function vehicleRemove(vehicle: Vehicle) {
    const res = await Api.subscriptionVehicleDelete(
      subscriptionId,
      vehicle.vehicleId,
      {
        vehicleId: vehicle.vehicleId,
        parkerId: selectedParker?.parker.parkerId,
      }
    );

    if (!res.isOk) {
      toast.error(res.error as string);
      return;
    }

    // ✅ update sub vehicles
    setSubVehicles?.((prev) =>
      prev.filter((v) => v.vehicleId !== vehicle.vehicleId)
    );

    // ✅ update parkers
    setParkers((prev) =>
      prev.map((p) =>
        p.parker && p.parker.parkerId === vehicle.parkerId
          ? {
              ...p,
              vehicles: p.vehicles.filter(
                (v) => v.vehicleId !== vehicle.vehicleId
              ),
            }
          : p
      )
    );

    // ✅ update selected parker
    setSelectedParker?.((prev) =>
      prev
        ? {
            ...prev,
            vehicles: prev.vehicles.filter(
              (v) => v.vehicleId !== vehicle.vehicleId
            ),
          }
        : prev
    );

    toast.success("Vehicle Removed Successfully");
  }

  async function parkerInvite(
    data: InviteParkerForm,
    groupId?: number | null,
    plan?: SubListEntry,
    shouldCommit?: boolean
  ) {
    const commit = shouldCommit ?? true;

    if (!commit) {
      setParkers((prev) => [
        ...prev,
        {
          draftId: crypto.randomUUID(),
          isDraft: true,
          name: data.name,
          email: data.emailAddress,
          phoneNumber: data.phoneNumber?.trim() || null,
          startDate: data.startDate, 
          groupId,
          vehicles: [],
          groupName:
            groupId != null
              ? groups!.find((g) => g.id === groupId)?.name ?? null
              : null,
        },
      ]);

      return;
    }

    // Use form value so subscriptionGroupId is always sent (including null for Unassigned).
    // Coerce to null when unset so the key is never undefined (JSON.stringify omits undefined).
    const subscriptionGroupIdValue =
      data.subscriptionGroupId === UNASSIGNED_GROUP_ID ||
      data.subscriptionGroupId == null
        ? null
        : data.subscriptionGroupId;

    const populatedSub = subscriptionId || plan!.subscriptionId;

    const invitePayload: Parameters<typeof Api.subParkerInvite>[1] = {
      emailAddress: data.emailAddress,
      name: data.name,
      phoneNumber: data.phoneNumber?.trim() || null,
      quantity: 1,
      subscriptionId: populatedSub,
      isLoginEnabled: data.isLoginEnabled,
      subscriptionGroupId: subscriptionGroupIdValue,
      startDate: localDateToInstantUtc(data.startDate)
    };

    const resp = await Api.subParkerInvite(populatedSub, invitePayload);

    processResult(
      resp,
      (val) => {
        toast.success("Parker Added Successfully");

        setParkers((prev) => [
          ...prev,
          {
            populatedSub,
            parkerId: val.parker.parkerId,
            name: val.parker.name,
            email: val.parker.emailAddress,
            quantity: val.quantity,
            isManaged: val.parker.isManaged,
            parker: parseParkerInfo(val.parker),
            vehicles: [],
            groupId: subscriptionGroupIdValue,
            groupName:
              subscriptionGroupIdValue != null
                ? groups!.find((g) => g.id === subscriptionGroupIdValue)?.name ??
                  null
                : null,
            startDate: val.startDate,
            subscriptionId: populatedSub,
            lpn: "N/A",
            plan: plan?.plan.name || "",
            sub: {
              subscriptionId: populatedSub,
              status: SubscriptionStatus.Active,
            },
            isDraft: false,
          },
        ]);
      },
      (err) => {
        if (err?.toLowerCase().includes("spots available=0")) {
          onNoSpaces?.();
        } else {
          toast.error(err);
        }
      }
    );
  }

  const openChooseModalForParkersList = async () => {
    if (!selectedParker) return;
    await openChooseModalForParker(selectedParker);
  };

  const openChooseModalForParker = async (parker: NonNullable<typeof selectedParker>) => {
    setSelectedParker?.(parker);
    const result = await Api.vehicleListByParker(parker.parker.parkerId);

    if (result.isOk) {
      setChooseModalVehicles(result.value);
    } else {
      setChooseModalVehicles([]);
    }
    setChooseModalExclude(parker.vehicles.map((x) => x.vehicleId));
    setShowChooseVehicle(true);
  };

  const openAddVehicleForParker = (parker: NonNullable<typeof selectedParker>) => {
    setSelectedParker?.(parker);
    setShowAddVehicle?.(true);
  };

  return {
    parkerRemove,
    parkerUndoCancel,
    vehicleAdd,
    vehicleLink,
    vehicleRemove,
    parkerInvite,
    openChooseModalForParkersList,
    openChooseModalForParker,
    openAddVehicleForParker,
  };
}
